/**
 * Shapes for `api/inventory/*`, from `Gopanza.Api.Services.Abstractions`.
 */

/** `InventoryMovementType`. Sale is recorded by the till, never from here. */
export const MOVEMENT_TYPE = {
  sale: 0,
  adjustmentUp: 1,
  adjustmentDown: 2,
  receiving: 3,
} as const;

/**
 * `InventoryMovementReason`.
 *
 * The dashboard maps the same enum for its loss panel (`LOSS_REASON_KEY` in
 * dashboard.dto). Nine of these count as loss there; the full set is wider,
 * which is why the two lists are not shared.
 */
export const MOVEMENT_REASON = {
  none: 0,
  damage: 1,
  lost: 2,
  found: 3,
  replenishment: 4,
  sampling: 5,
  gift: 6,
  spoiled: 7,
  transferToKitchen: 8,
  transferToOtherStore: 9,
  transferToDeli: 10,
  layaway: 11,
} as const;

/** `InventoryMovementSort` — by quantity on hand. */
export const SORT = { default: 0, ascending: 1, descending: 2 } as const;

export interface InventoryItem {
  /** The `BusinessItem` id — what every write on this page is keyed by. */
  id: string;
  itemId: string | null;
  quantityOnHand: number;
  pack: number;
  unitsPerPackage: number;
  isUnitForRelatedItems: boolean;
  hasRelatedItems: boolean;
  affectsInventory: boolean;
  upc: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  itemImage: string | null;
  sectionName: string | null;
  superSectionName: string | null;
  department: string | null;
  isVisible: boolean;
  isAvailable: boolean;
  cost: number;
  costOfBox: number;
  hasPluCode: boolean;
  hasRandomWeight: boolean;
}

export interface InventorySearchResult {
  result: InventoryItem[];
  total: number;
}

export interface InventoryMovement {
  id: string;
  createdAt: string;
  type: number;
  reason: number;
  amount: number;
  businessItemId: string | null;
  comments: string | null;
  registeredById: string | null;
  registeredBy: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    completeName?: string | null;
    avatar?: string | null;
  } | null;
}

export interface InventoryHistory {
  result: InventoryMovement[];
  total: number;
}

/** What `inventory/register` accepts. */
export interface MovementInput {
  businessItemId: string;
  amount: number;
  comments: string;
  inventoryChangeDate: string | null;
  type: number;
  reason: number;
}

/**
 * What `inventory/update/item` accepts. Every field is nullable and only the
 * ones sent are written, so a partial update is the normal case.
 */
export interface FlagsInput {
  businessItemId: string;
  affectsInventory?: boolean;
  isVisible?: boolean;
  isAvailable?: boolean;
  quantityOnHand?: number;
  cost?: number;
  costOfBox?: number;
  pack?: number;
  price?: number;
}

export function itemLabel(item: InventoryItem): string {
  return [item.brand, item.name, item.description].filter(Boolean).join(' ');
}

/** Movements that add stock, for reading a signed amount out of the type. */
export function isIncrease(type: number): boolean {
  return type === MOVEMENT_TYPE.adjustmentUp || type === MOVEMENT_TYPE.receiving;
}

/**
 * A count expressed in whole packs plus the units left over.
 *
 * `unitsPerPackage` of 0 means "not packed", and dividing by it would give
 * Infinity — the original guarded that by treating 0 as 1, which reports every
 * unit as its own pack; saying nothing is more honest.
 */
export function packBreakdown(
  quantity: number,
  unitsPerPackage: number
): { packs: number; units: number } | null {
  if (!unitsPerPackage || unitsPerPackage <= 1) return null;
  const packs = Math.floor(quantity / unitsPerPackage);
  return { packs, units: quantity - packs * unitsPerPackage };
}
